import { DOCUMENT_VARIABLE_METADATA_BY_KEY } from '@idc-forms/schemas/variables/document/document-catalog'
import type { DocumentVariableMetadata } from '@idc-forms/schemas/variables/document/document-variable'
import type { NegotiationVariableContext } from './context'
import { HOST_ENUM_VALUES } from './host-options'
import type { VariableDef } from './types'

/**
 * Helper de autoría de una variable de negociación: REFERENCIA el metadato canónico y le cuelga su
 * `resolve` (ADR 0036).
 *
 * Antes declaraba el metadato acá mismo. Ahora la declaración vive en
 * `@idc-forms/schemas/variables/document/*` —compartida con la API— y lo que queda co-locado es lo
 * único que no puede viajar: la resolución contra `NegotiationVariableContext`.
 *
 * Para EXPONER UN DATO NUEVO son dos pasos y en este orden: declarás el metadato en el paquete y
 * le colgás el `resolve` en el archivo de dominio que corresponda. El sidebar, el condicional y la
 * generación del PDF lo toman automáticamente.
 *
 * ⛔ NO lanza si la key no existe. Devuelve un metadato vacío que `checkRegistryIntegrity` reporta
 * como «sin metadato canónico»: un `throw` acá dejaría la pantalla en blanco en producción por un
 * error de autoría que el test del registry ya atrapa en CI.
 */

type Resolve = (ctx: NegotiationVariableContext) => string

const ORPHAN = (key: string): DocumentVariableMetadata => ({
  key,
  label: key,
  category: 'other',
  type: 'string',
  // Sin scopes: es la marca que hace que `checkCatalogIntegrity` lo cante.
  scopes: []
})

/** El metadato canónico → la forma que ve el editor. La ÚNICA traducción del catálogo. */
export const fromMetadata = (
  metadata: DocumentVariableMetadata,
  resolve: Resolve
): NegotiationVariable => ({
  key: metadata.key,
  label: metadata.label,
  category: metadata.category,
  type: metadata.type,
  group: metadata.group,
  description: metadata.description,
  // `options` (canónico) → `enumValues` (legacy del editor). El renombre aguas abajo es de la
  // ola 3: acá se traduce y ni el picker ni el condicional se enteran.
  enumValues: metadata.optionsFromHost
    ? HOST_ENUM_VALUES[metadata.key]
    : metadata.options && [...metadata.options],
  example: metadata.example,
  masks: metadata.masks && [...metadata.masks],
  resolve
})

export const defineVariable = (key: string, resolve: Resolve): NegotiationVariable =>
  fromMetadata(DOCUMENT_VARIABLE_METADATA_BY_KEY.get(key) ?? ORPHAN(key), resolve)

export type NegotiationVariable = VariableDef<NegotiationVariableContext>
